import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, formatDate } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { Complaint, ComplaintCategory, ComplaintChannel, ComplaintState, Page, RoutingDetermination } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge } from '../../ui/components';

const OMBUDSMAN_ROLES = ['OMBUDSMAN_OFFICER'];
const OFFICER_OR_ACCS_ROLES = ['OMBUDSMAN_OFFICER', 'ACCS_INVESTIGATOR'];
const CHANNELS: ComplaintChannel[] = ['WEB', 'PHONE', 'WALK_IN', 'POST', 'EMAIL', 'MOBILE_APP'];
const CATEGORIES: ComplaintCategory[] = [
  'MALADMINISTRATION',
  'UNREASONABLE_DELAY',
  'DISCOURTESY',
  'UNFAIR_TREATMENT',
  'DENIAL_OF_SERVICE',
  'PROCUREMENT_IRREGULARITY',
  'CORRUPTION_SUSPECTED',
  'OTHER',
];
const STATES: ComplaintState[] = ['RECEIVED', 'REFERRED_TO_MDA', 'UNDER_INVESTIGATION', 'REFERRED_TO_ACCS', 'AWAITING_COMPLAINANT', 'RESOLVED', 'CLOSED', 'SIGNPOSTED', 'WITHDRAWN'];
const ROUTINGS: RoutingDetermination[] = ['OMBUDSMAN', 'ACCS', 'MDA_INTERNAL', 'OUT_OF_JURISDICTION'];

/** GET/POST /api/v1/m40/complaints — the single front door for complaints about government. */
export function Complaints() {
  const { persona } = useAuth();
  const roles = persona?.roles ?? [];
  const isOmbudsmanOfficer = roles.includes('OMBUDSMAN_OFFICER');
  const canViewCaseload = roles.some((r) => OFFICER_OR_ACCS_ROLES.includes(r));
  const navigate = useNavigate();
  const [status, setStatus] = useState<ComplaintState | ''>('');
  const [routing, setRouting] = useState<RoutingDetermination | ''>('');
  const [q, setQ] = useState('');
  const [showFile, setShowFile] = useState(false);

  const qp = new URLSearchParams({ limit: '50' });
  if (status) qp.set('status', status);
  if (routing) qp.set('routing', routing);
  if (q.trim()) qp.set('q', q.trim());
  const caseloadQ = useApiQuery<Page<Complaint>>(canViewCaseload ? `/api/v1/m40/complaints?${qp.toString()}` : null);
  const mineQ = useApiQuery<Page<Complaint>>(!canViewCaseload ? '/api/v1/m40/complaints/me?limit=50' : null);

  return (
    <div>
      <PageHeader
        title="Complaints"
        subtitle="Single front door for complaints about government — every complainant gets a track reference regardless of eventual routing."
        actions={
          <button className="btn-primary" onClick={() => setShowFile(true)}>
            + {isOmbudsmanOfficer ? 'Officer-assisted intake' : 'File a complaint'}
          </button>
        }
      />

      {canViewCaseload ? (
        <>
          <div className="mb-4 flex flex-wrap items-end gap-3">
            <Field label="Ref no. / MDA / narrative contains" className="min-w-[220px] flex-1">
              <input className="field-input" value={q} onChange={(e) => setQ(e.target.value)} />
            </Field>
            <Field label="Routing" className="!mb-0">
              <select className="field-input !w-auto" value={routing} onChange={(e) => setRouting(e.target.value as RoutingDetermination | '')}>
                <option value="">All</option>
                {ROUTINGS.map((r) => (
                  <option key={r} value={r}>
                    {r.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Status" className="!mb-0">
              <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as ComplaintState | '')}>
                <option value="">All</option>
                {STATES.map((s) => (
                  <option key={s} value={s}>
                    {s.replaceAll('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          {!isOmbudsmanOfficer ? (
            <p className="mb-3 text-xs text-slate-400">
              Signed in as ACCS_INVESTIGATOR without OMBUDSMAN_OFFICER — this view is scoped to complaints already routed to ACCS only.
            </p>
          ) : null}
          {caseloadQ.error ? <ErrorNote message={caseloadQ.error} /> : null}
          <div className="card overflow-x-auto">
            {caseloadQ.loading ? (
              <Spinner />
            ) : (caseloadQ.data?.items.length ?? 0) === 0 ? (
              <EmptyState title="No complaints match" />
            ) : (
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Subject MDA</th>
                    <th className="table-head">Category</th>
                    <th className="table-head">Routing</th>
                    <th className="table-head">State</th>
                    <th className="table-head">Filed</th>
                  </tr>
                </thead>
                <tbody>
                  {caseloadQ.data!.items.map((c) => (
                    <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ombudsman/complaints/${c.id}`)}>
                      <td className="table-cell font-mono text-xs">{c.refNo}</td>
                      <td className="table-cell font-medium">{c.subjectMda}</td>
                      <td className="table-cell text-xs">{c.category.replaceAll('_', ' ')}</td>
                      <td className="table-cell text-xs">{c.routingDetermination?.replaceAll('_', ' ') ?? '—'}</td>
                      <td className="table-cell">
                        <StatusBadge status={c.state} />
                      </td>
                      <td className="table-cell text-xs">{formatDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : (
        <>
          <RoleNotice roles={OMBUDSMAN_ROLES} />
          <h2 className="mb-2 text-sm font-bold text-slate-700">My complaints</h2>
          {mineQ.loading ? (
            <Spinner />
          ) : mineQ.error ? (
            <ErrorNote message={mineQ.error} />
          ) : (mineQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="You have not filed a complaint" />
          ) : (
            <div className="card overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="table-head">Ref</th>
                    <th className="table-head">Subject MDA</th>
                    <th className="table-head">State</th>
                    <th className="table-head">Filed</th>
                  </tr>
                </thead>
                <tbody>
                  {mineQ.data!.items.map((c) => (
                    <tr key={c.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ombudsman/complaints/${c.id}`)}>
                      <td className="table-cell font-mono text-xs">{c.refNo}</td>
                      <td className="table-cell font-medium">{c.subjectMda}</td>
                      <td className="table-cell">
                        <StatusBadge status={c.state} />
                      </td>
                      <td className="table-cell text-xs">{formatDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {showFile ? (
        <FileComplaintModal
          isOfficer={isOmbudsmanOfficer}
          onClose={() => setShowFile(false)}
          onDone={(created) => {
            setShowFile(false);
            if (canViewCaseload) navigate(`/ombudsman/complaints/${created.id}`);
            else mineQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function FileComplaintModal({ isOfficer, onClose, onDone }: { isOfficer: boolean; onClose: () => void; onDone: (c: Complaint) => void }) {
  const [channel, setChannel] = useState<ComplaintChannel>(isOfficer ? 'PHONE' : 'WEB');
  const [complainantNin, setComplainantNin] = useState('');
  const [complainantName, setComplainantName] = useState('');
  const [complainantPhone, setComplainantPhone] = useState('');
  const [complainantEmail, setComplainantEmail] = useState('');
  const [subjectMda, setSubjectMda] = useState('');
  const [category, setCategory] = useState<ComplaintCategory>('MALADMINISTRATION');
  const [narrative, setNarrative] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<Complaint>('/api/v1/m40/complaints', {
        channel,
        complainantNin: isOfficer && complainantNin ? complainantNin : undefined,
        complainantName,
        complainantPhone: complainantPhone || undefined,
        complainantEmail: complainantEmail || undefined,
        subjectMda,
        category,
        narrative,
        evidence: [],
      });
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to file complaint');
    } finally {
      setBusy(false);
    }
  };

  const valid = complainantName.trim().length >= 2 && subjectMda.trim().length >= 2 && narrative.trim().length >= 10;

  return (
    <Modal title={isOfficer ? 'Officer-assisted complaint intake' : 'File a complaint'} onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Channel">
            <select className="field-input" value={channel} onChange={(e) => setChannel(e.target.value as ComplaintChannel)}>
              {CHANNELS.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Category">
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as ComplaintCategory)}>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <Field label="Subject MDA (which ministry/department/agency)">
          <input className="field-input" value={subjectMda} onChange={(e) => setSubjectMda(e.target.value)} placeholder="e.g. Ministry of Land Use and Housing" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Complainant name">
            <input className="field-input" value={complainantName} onChange={(e) => setComplainantName(e.target.value)} />
          </Field>
          {isOfficer ? (
            <Field label="Complainant NIN (officer-assisted only)">
              <input className="field-input" value={complainantNin} onChange={(e) => setComplainantNin(e.target.value)} />
            </Field>
          ) : (
            <Field label="Complainant phone (optional)">
              <input className="field-input" value={complainantPhone} onChange={(e) => setComplainantPhone(e.target.value)} />
            </Field>
          )}
        </div>
        {isOfficer ? (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Complainant phone (optional)">
              <input className="field-input" value={complainantPhone} onChange={(e) => setComplainantPhone(e.target.value)} />
            </Field>
            <Field label="Complainant email (optional)">
              <input className="field-input" value={complainantEmail} onChange={(e) => setComplainantEmail(e.target.value)} />
            </Field>
          </div>
        ) : (
          <Field label="Complainant email (optional)">
            <input className="field-input" value={complainantEmail} onChange={(e) => setComplainantEmail(e.target.value)} />
          </Field>
        )}
        <Field label="Narrative">
          <textarea className="field-input" rows={4} value={narrative} onChange={(e) => setNarrative(e.target.value)} placeholder="What happened, when, and what outcome is sought…" />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Filing…' : 'File complaint'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
